/**
 * 트리거 버튼 기준 드롭다운 위치 계산 (portal + `position: fixed` 메뉴용).
 *
 * 아래로만 여는 계산은 목록 하단 항목에서 메뉴가 뷰포트 밖으로 잘려 안 보인다
 * (모바일 문서관리 목록의 `...` 메뉴). 공간이 부족하면 버튼 위로 뒤집는다.
 * `NotificationDropdown`이 쓰는 flip 규칙과 같은 방식이다.
 */

/** 트리거 버튼의 뷰포트 기준 위치 (`getBoundingClientRect()`의 필요한 필드만). */
export interface TriggerRect {
  top: number;
  bottom: number;
  right: number;
}

export interface DropdownPositionInput {
  trigger: TriggerRect;
  /** 실제 렌더된 메뉴 크기 (측정 전이면 0 — 이때는 아래로 두고 다음 측정에서 교정). */
  menuWidth: number;
  menuHeight: number;
  viewportWidth: number;
  viewportHeight: number;
}

/** 트리거와 메뉴 사이 간격. */
const GAP = 4;
/** 뷰포트 가장자리 최소 여백. */
const EDGE_PADDING = 8;

/**
 * 메뉴의 `fixed` 좌표를 계산한다.
 *
 * 세로: 아래 공간이 모자라고 위가 더 넓으면 버튼 위로 뒤집는다. 양쪽 다 모자라면
 * 더 넓은 쪽에 붙이고 뷰포트 안으로 clamp한다(잘려도 최대한 보이게).
 * 가로: 버튼 오른쪽 정렬을 기본으로 하되 화면 밖으로 나가지 않게 clamp한다.
 */
export function computeDropdownPosition({
  trigger,
  menuWidth,
  menuHeight,
  viewportWidth,
  viewportHeight,
}: DropdownPositionInput): { top: number; left: number; flipped: boolean } {
  const spaceBelow = viewportHeight - trigger.bottom - GAP - EDGE_PADDING;
  const spaceAbove = trigger.top - GAP - EDGE_PADDING;

  // 아래에 들어가면 아래로. 안 들어가면 위가 더 넓을 때만 뒤집는다.
  const fitsBelow = menuHeight <= spaceBelow;
  const flipped = !fitsBelow && spaceAbove > spaceBelow;

  const rawTop = flipped ? trigger.top - GAP - menuHeight : trigger.bottom + GAP;
  // clamp 하한(EDGE_PADDING)이 상한보다 커질 수 있다(메뉴가 뷰포트보다 긴 경우).
  // Math.min을 먼저 걸면 음수 top이 나오므로 max를 마지막에 둔다.
  const top = Math.max(EDGE_PADDING, Math.min(rawTop, viewportHeight - menuHeight - EDGE_PADDING));

  const rawLeft = trigger.right - menuWidth;
  const left = Math.max(EDGE_PADDING, Math.min(rawLeft, viewportWidth - menuWidth - EDGE_PADDING));

  return { top, left, flipped };
}
